import { format, isLeapYear as isLeapYearFns, parse } from 'date-fns';

import { SYSTEM_DATE_PATTERN } from '../constants';

export const dateFormatFromJSON = (
  date: Date | string,
  pattern = SYSTEM_DATE_PATTERN,
  timeZone = 'UTC'
): string => {
  if (typeof date === 'object') {
    date = changeTimeZone(date, timeZone);
  }

  return format(date, pattern);
};

const changeTimeZone = (date: Date | string, timeZone: string) => {
  if (typeof date === 'string') {
    return new Date(
      new Date(date).toLocaleString('en-US', {
        timeZone,
      })
    );
  }

  return new Date(
    date.toLocaleString('en-US', {
      timeZone,
    })
  );
};

interface IChangeFormatProps {
  date: string;
  fromPattern: string;
  toPattern: string;
}

export const changeFormat = ({
  date,
  fromPattern,
  toPattern,
}: IChangeFormatProps): string => {
  return dateFormatFromJSON(parse(date, fromPattern, new Date()), toPattern);
};

export const formatKebabCase = (
  str: string,
  separator: RegExp | string = ' '
): string => str.split(separator).join('-').toLowerCase();

export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.trim().replace(/\D/g, '');
  const match = cleaned.match(/^(\d)(\d{3})(\d{3})(\d{4})$/);
  const match2 = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  const match3 = cleaned.match(/^(\d{3})(\d{4})$/);
  if (match) {
    return match[1] + ' (' + match[2] + ') ' + match[3] + '-' + match[4];
  }
  if (match2) {
    return '(' + match2[1] + ') ' + match2[2] + '-' + match2[3];
  }
  if (match3) {
    return match3[1] + '-' + match3[2];
  }
  return phone;
};

type IQueryValue = boolean | number | string | null;
export const queryStringFormat = (
  params: Record<string, IQueryValue>
): string => {
  const search = new URLSearchParams();
  for (const key in params) {
    const value = params[key];
    if (value === null) continue;
    search.append(key, value.toString());
  }
  return search.toString();
};

export const formatFullName = <
  T extends { firstName: string; lastName: string },
>(
  person: T
): string => `${person.firstName || ''} ${person.lastName || ''}`.trim();

export const isLeapYear = (year: number): boolean => {
  return isLeapYearFns(new Date(year, 0, 1));
};

export const daysOfMonth = (month: number, year: number) => {
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => ({
    key: String(i + 1).padStart(2, '0'),
    value: String(i + 1).padStart(2, '0'),
  }));
};

const SizeUnit = {
  K: 1000,
  M: 1000 ** 2,
  G: 1000 ** 3,
} as const;

export const formatSize = (size: number, sizeUnit = 'B'): string => {
  switch (true) {
    case size < SizeUnit.K.valueOf():
      return `${size.toFixed(0)} ${sizeUnit}`;
    case size < SizeUnit.M.valueOf():
      return `${(size / SizeUnit.K).toFixed(0)} K${sizeUnit}`;
    case size < SizeUnit.G.valueOf():
      return `${(size / SizeUnit.M).toFixed(0)} M${sizeUnit}`;
    default:
      return `${(size / SizeUnit.G).toFixed(0)} G${sizeUnit}`;
  }
};
